// react
import React, { useEffect, useRef, useState } from 'react';

// redux
import { useSelector, useDispatch } from 'react-redux';
import { BACKGROUND_UPDATE } from 'redux/types/background.types';
import { IS_SAVE } from 'redux/types/isSave.types';

// styled components
import { StyledBackground } from './StyledBackground';

// utils
import { COLOR_SWATCHES, normalizeHexInput } from 'utils/background';
import { ACCEPT_ATTR, validateBackgroundFile } from 'utils/backgroundImageFile';

// i18n
import { useTranslation } from 'react-i18next';

// plugin
import clsx from 'classnames';

const MODES = [
    { key: 'default', label: 'background.default' },
    { key: 'color', label: 'background.color' },
    { key: 'image', label: 'background.image' },
];

// 「背景」分頁面板。改動只存在 redux（store.background），按上方「儲存」時由 NavTop 一起送出；
// 選的圖片也是到儲存時才上傳（見 NavTop handleSaveAll）
const Background = () => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const background = useSelector((store) => store.background);
    const fileInputRef = useRef(null);

    const [hexText, setHexText] = useState(background.color);
    const [hexInvalid, setHexInvalid] = useState(false);
    const [fileError, setFileError] = useState('');

    // 換模型、或用色票 / 色盤改了顏色時，同步文字框
    useEffect(() => {
        setHexText(background.color);
        setHexInvalid(false);
    }, [background.color]);

    const update = (payload) => {
        dispatch({ type: BACKGROUND_UPDATE, payload });
        dispatch({ type: IS_SAVE, payload: true });
    };

    const handleHexChange = (e) => {
        const text = e.target.value;
        setHexText(text);
        const normalized = normalizeHexInput(text);
        setHexInvalid(!normalized);
        if (normalized) update({ color: normalized, mode: 'color' });
    };

    const handleFileChange = (e) => {
        const file = e.target.files?.[0];
        // 清掉 value，同一個檔案再選一次也會觸發 onChange
        e.target.value = '';
        if (!file) return;
        const errorKey = validateBackgroundFile(file);
        if (errorKey) {
            setFileError(t(`background.${errorKey === 'backgroundInvalidType' ? 'invalidType' : 'tooLarge'}`));
            return;
        }
        setFileError('');
        if (background.previewUrl) URL.revokeObjectURL(background.previewUrl);
        update({ mode: 'image', pendingFile: file, previewUrl: URL.createObjectURL(file) });
    };

    const imageUrl = background.previewUrl || background.bgImage;

    return (
        <StyledBackground>
            <div className="modes" role="radiogroup" aria-label={t('background.title')}>
                {MODES.map((mode) => (
                    <button
                        key={mode.key}
                        type="button"
                        role="radio"
                        aria-checked={background.mode === mode.key}
                        className={clsx('mode', { active: background.mode === mode.key })}
                        onClick={() => update({ mode: mode.key })}
                    >
                        {t(mode.label)}
                    </button>
                ))}
            </div>

            {background.mode === 'default' && <div className="hint">{t('background.defaultHint')}</div>}

            {background.mode === 'color' && (
                <>
                    <div className="label">{t('background.swatches')}</div>
                    <div className="swatches">
                        {COLOR_SWATCHES.map((color) => (
                            <button
                                key={color}
                                type="button"
                                className={clsx('swatch', { active: background.color === color })}
                                style={{ background: color }}
                                aria-label={color}
                                aria-pressed={background.color === color}
                                onClick={() => update({ color, mode: 'color' })}
                            />
                        ))}
                    </div>
                    <div className="label">{t('background.customColor')}</div>
                    <div className="customColor">
                        <input
                            type="color"
                            value={background.color}
                            aria-label={t('background.customColor')}
                            onChange={(e) => update({ color: e.target.value.toUpperCase(), mode: 'color' })}
                        />
                        <input
                            type="text"
                            value={hexText}
                            maxLength={7}
                            spellCheck={false}
                            className={clsx({ invalid: hexInvalid })}
                            aria-invalid={hexInvalid}
                            aria-label={t('background.customColor')}
                            onChange={handleHexChange}
                        />
                    </div>
                    {hexInvalid && <div className="error">{t('background.hexInvalid')}</div>}
                </>
            )}

            {background.mode === 'image' && (
                <>
                    <div
                        className="imagePreview"
                        style={imageUrl ? { backgroundImage: `url("${imageUrl.replace(/"/g, '\\"')}")` } : undefined}
                    >
                        {!imageUrl && t('background.noImage')}
                    </div>
                    <button type="button" className="uploadButton" onClick={() => fileInputRef.current?.click()}>
                        {imageUrl ? t('background.replace') : t('background.upload')}
                    </button>
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept={ACCEPT_ATTR}
                        className="visuallyHidden"
                        tabIndex={-1}
                        aria-hidden="true"
                        onChange={handleFileChange}
                    />
                    {fileError ? <div className="error">{fileError}</div> : <div className="hint">{t('background.imageHint')}</div>}
                </>
            )}

            <div className="hint arNote">{t('background.arNote')}</div>
        </StyledBackground>
    );
};

export default Background;
